(() => {
  const inputSelector = '[data-yizhuo-filter-input]';
  const cardSelector = '#product-grid > [data-yizhuo-filter-card]';
  const categorySearchSelector = '[data-yizhuo-category-search]';
  let observerStarted = false;
  let applyQueued = false;

  const getSelectedFilters = () => {
    const selected = {
      vendor: new Set(),
      type: new Set(),
      tag: new Set(),
    };

    document.querySelectorAll(`${inputSelector}:checked`).forEach((input) => {
      const group = input.dataset.yizhuoFilterGroup;
      if (selected[group]) selected[group].add(input.value);
    });

    return selected;
  };

  const hasActiveFilters = (selected) => Object.values(selected).some((values) => values.size > 0);

  const matchesCard = (card, selected) => {
    if (selected.vendor.size > 0 && !selected.vendor.has(card.dataset.yizhuoVendor || '')) return false;
    if (selected.type.size > 0 && !selected.type.has(card.dataset.yizhuoType || '')) return false;

    if (selected.tag.size > 0) {
      const tags = new Set((card.dataset.yizhuoTags || '').split(',').filter(Boolean));
      const hasTag = Array.from(selected.tag).some((tag) => tags.has(tag));
      if (!hasTag) return false;
    }

    return true;
  };

  const getColumnCount = (grid) => {
    if (!grid) return 1;
    const width = window.innerWidth;

    if (width >= 990) {
      const match = grid.className.match(/grid--(\\d)-col-desktop/);
      return match ? Number(match[1]) : 4;
    }

    const tabletMatch = grid.className.match(/grid--(\\d)-col-tablet-down/);
    return tabletMatch ? Number(tabletMatch[1]) : 2;
  };

  const updateRowDividers = (grid, visibleCards, active) => {
    grid.querySelectorAll('.yizhuo-visible-row-start').forEach((card) => {
      card.classList.remove('yizhuo-visible-row-start');
      card.style.removeProperty('--yizhuo-row-divider-width');
    });

    grid.classList.toggle('yizhuo-filter-active', active);
    if (!active) return;

    const columns = getColumnCount(grid);
    const dividerWidth = `${Math.round(grid.getBoundingClientRect().width)}px`;
    visibleCards.forEach((card, index) => {
      if (index >= columns && index % columns === 0) {
        card.classList.add('yizhuo-visible-row-start');
        card.style.setProperty('--yizhuo-row-divider-width', dividerWidth);
      }
    });
  };

  const putAvailableProductsFirst = (grid, cards) => {
    const available = cards.filter((card) => card.dataset.yizhuoAvailable === 'true');
    const unavailable = cards.filter((card) => card.dataset.yizhuoAvailable !== 'true');
    const ordered = [...available, ...unavailable];

    ordered.forEach((card, index) => {
      if (grid.children[index] !== card) grid.append(card);
    });
  };

  const applyFilters = () => {
    applyQueued = false;
    const grid = document.getElementById('product-grid');
    const cards = Array.from(document.querySelectorAll(cardSelector));
    if (!grid || cards.length === 0) return;

    putAvailableProductsFirst(grid, cards);

    const selected = getSelectedFilters();
    const active = hasActiveFilters(selected);
    const visibleCards = [];

    cards.forEach((card) => {
      const visible = !active || matchesCard(card, selected);
      card.hidden = !visible;
      card.classList.toggle('yizhuo-filter-hidden', !visible);
      card.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) visibleCards.push(card);
    });

    updateRowDividers(grid, visibleCards, active);

    document.querySelectorAll(inputSelector).forEach((input) => {
      const label = input.closest('label');
      if (label) label.classList.toggle('active', input.checked);
    });

    document.querySelectorAll('[data-yizhuo-filter-status]').forEach((status) => {
      const template = status.closest('[data-yizhuo-status-template]')?.dataset.yizhuoStatusTemplate || '[shown] of [total] products shown';
      status.textContent = active ? template.replace('[shown]', visibleCards.length).replace('[total]', cards.length) : '';
    });

    document.querySelectorAll('[data-yizhuo-secondary-clear]').forEach((button) => {
      button.hidden = !active;
    });

    document.querySelectorAll('[data-yizhuo-filter-empty]').forEach((emptyState) => {
      emptyState.classList.toggle('hidden', !active || visibleCards.length > 0);
    });

    document.querySelectorAll('.pagination-wrapper').forEach((pagination) => {
      pagination.hidden = active;
    });
  };

  const queueApplyFilters = () => {
    if (applyQueued) return;
    applyQueued = true;
    requestAnimationFrame(applyFilters);
  };

  const syncMatchingInputs = (changedInput) => {
    document.querySelectorAll(inputSelector).forEach((input) => {
      if (input === changedInput) return;
      if (input.dataset.yizhuoFilterGroup === changedInput.dataset.yizhuoFilterGroup && input.value === changedInput.value) {
        input.checked = changedInput.checked;
      }
    });
  };

  const initInputs = () => {
    document.querySelectorAll(inputSelector).forEach((input) => {
      if (input.dataset.yizhuoFilterReady === 'true') return;
      input.dataset.yizhuoFilterReady = 'true';
      input.addEventListener('input', (event) => event.stopPropagation());
      input.addEventListener('change', () => {
        syncMatchingInputs(input);
        queueApplyFilters();
      });
    });

    document.querySelectorAll('[data-yizhuo-secondary-clear]').forEach((button) => {
      if (button.dataset.yizhuoFilterReady === 'true') return;
      button.dataset.yizhuoFilterReady = 'true';
      button.addEventListener('click', () => {
        document.querySelectorAll(inputSelector).forEach((input) => {
          input.checked = false;
        });
        queueApplyFilters();
      });
    });
  };

  const initCategorySearch = () => {
    document.querySelectorAll(categorySearchSelector).forEach((search) => {
      if (search.dataset.yizhuoCategorySearchReady === 'true') return;

      const nav = search.closest('.yizhuo-category-nav');
      const input = search.querySelector('[data-yizhuo-category-search-input]');
      const clear = search.querySelector('[data-yizhuo-category-search-clear]');
      const status = search.querySelector('[data-yizhuo-category-search-status]');
      const items = nav ? Array.from(nav.querySelectorAll('[data-yizhuo-category-item]')) : [];
      if (!nav || !input || !clear || !status) return;

      search.dataset.yizhuoCategorySearchReady = 'true';
      search.hidden = false;

      const update = () => {
        const query = input.value.trim().toLowerCase();
        let matchingCount = 0;

        items.forEach((item) => {
          const label = item.textContent.trim().toLowerCase();
          const matches = query === '' || label.includes(query);
          item.hidden = !matches;
          if (matches) matchingCount += 1;
        });

        clear.hidden = query === '';

        if (query === '') {
          status.textContent = `${items.length} categories available.`;
        } else if (matchingCount === 0) {
          status.textContent = 'No matching categories. Clear the search to show all categories.';
        } else {
          status.textContent = `${matchingCount} categories found.`;
        }
      };

      input.addEventListener('input', (event) => {
        event.stopPropagation();
        update();
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      });
      clear.addEventListener('click', () => {
        input.value = '';
        update();
        input.focus();
      });
      update();
    });
  };

  const startObserver = () => {
    if (observerStarted) return;
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    observerStarted = true;
    new MutationObserver(() => {
      initCategorySearch();
      queueApplyFilters();
    }).observe(container, { childList: true, subtree: true });
  };

  const init = () => {
    initInputs();
    initCategorySearch();
    startObserver();
    queueApplyFilters();
  };

  document.addEventListener('DOMContentLoaded', init);
  window.addEventListener('resize', queueApplyFilters);
  init();
})();
